import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Wrench, Plus, Search, Play, Zap, Shield, Server, Database, Globe, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const RISK_COLORS = { LOW: 'text-[var(--nexus-success)]', MEDIUM: 'text-[var(--nexus-warning)]', HIGH: 'text-[var(--nexus-danger)]' };
const PLATFORM_COLORS = { kaseya: '#00A4E4', solarwinds: '#FC6D26', aws: '#FF9900', generic: '#94A3B8' };

export default function RunbookMarketplacePage() {
  const token = localStorage.getItem('nexus_token');
  const queryClient = useQueryClient();
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [nlpDescription, setNlpDescription] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const { data: runbooks } = useQuery({
    queryKey: ['runbooks', filterPlatform, filterCategory],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterPlatform) params.set('platform', filterPlatform);
      if (filterCategory) params.set('category', filterCategory);
      const { data } = await axios.get(`${BACKEND_URL}/api/runbooks?${params}`, { headers });
      return data;
    },
  });

  const { data: categories } = useQuery({
    queryKey: ['runbook-categories'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/runbooks/categories`, { headers }); return data; },
  });

  const generateMutation = useMutation({
    mutationFn: async (desc) => { const { data } = await axios.post(`${BACKEND_URL}/api/runbooks/generate`, { description: desc }, { headers }); return data; },
    onSuccess: (data) => {
      if (data.error) { toast.error(data.error); return; }
      toast.success(`Runbook created: ${data.name}`); setShowBuilder(false); setNlpDescription(''); queryClient.invalidateQueries(['runbooks']);
    },
    onError: () => toast.error('Generation failed'),
  });

  const allRunbooks = [...(runbooks?.builtin || []), ...(runbooks?.custom || [])];

  return (
    <div data-testid="runbook-marketplace" className="w-full max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)]">Runbook Marketplace</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">{allRunbooks.length} runbooks • ARIA auto-selects based on incident type</p></div>
        <button onClick={() => setShowBuilder(!showBuilder)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0d3d4d] border border-cyan-500/30 text-[var(--nexus-accent)] text-xs font-semibold hover:bg-[#0f4a5c]">
          <Zap size={12} /> NLP Builder
        </button>
      </div>

      {showBuilder && (
        <div className="nexus-card p-4 mb-4 animate-nexus-slide-in">
          <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2">Natural Language Runbook Builder</h3>
          <p className="text-[10px] text-[var(--nexus-text-muted)] mb-3">Describe what you want in plain English. Claude converts it to an executable runbook.</p>
          <textarea value={nlpDescription} onChange={e => setNlpDescription(e.target.value)} rows={3}
            placeholder='e.g., "When disk usage exceeds 90%, delete files older than 30 days in /var/log, then restart syslog"'
            className="w-full px-3 py-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-xs text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)] resize-none" />
          <button onClick={() => generateMutation.mutate(nlpDescription)} disabled={!nlpDescription.trim() || generateMutation.isPending}
            className="mt-2 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--nexus-accent)] text-white text-xs font-medium disabled:opacity-50">
            <Send size={12} /> {generateMutation.isPending ? 'Generating...' : 'Generate Runbook'}
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {['', 'kaseya', 'solarwinds', 'aws', 'generic'].map(p => (
          <button key={p} onClick={() => setFilterPlatform(p)}
            className={`px-2.5 py-1 rounded text-[10px] font-medium border ${filterPlatform === p ? 'bg-[#0d2847] text-[var(--nexus-primary-bright)] border-blue-500/30' : 'bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-muted)] border-[var(--nexus-border)]'}`}>
            {p || 'All Platforms'}
          </button>
        ))}
        <div className="w-px h-4 bg-[var(--nexus-border)]" />
        {(categories?.categories || []).map(c => (
          <button key={c} onClick={() => setFilterCategory(filterCategory === c ? '' : c)}
            className={`px-2 py-1 rounded text-[10px] border ${filterCategory === c ? 'bg-[#0d3d4d] text-[var(--nexus-accent)] border-cyan-500/30' : 'border-[var(--nexus-border)] text-[var(--nexus-text-muted)]'}`}>{c}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="runbook-grid">
        {allRunbooks.map(rb => (
          <div key={rb.id} className="nexus-card relative overflow-hidden p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)]">{rb.name}</h3>
                <p className="text-[10px] text-[var(--nexus-text-muted)] mt-0.5">{rb.description}</p>
              </div>
              <span className={`font-mono text-[10px] font-bold ${RISK_COLORS[rb.risk] || ''}`}>{rb.risk}</span>
            </div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {(rb.platform || []).map(p => <span key={p} className="font-mono text-[9px] px-1.5 py-0.5 rounded border" style={{ color: PLATFORM_COLORS[p] || '#94A3B8', borderColor: `${PLATFORM_COLORS[p] || '#94A3B8'}40` }}>{p}</span>)}
              <span className="text-[9px] text-[var(--nexus-text-muted)]">{rb.category}</span>
              {rb.auto_trigger && <span className="text-[9px] px-1 rounded bg-[#0d3d1d] text-green-300 border border-green-500/30">auto-trigger</span>}
              {rb.created_by === 'NLP_BUILDER' && <span className="text-[9px] px-1 rounded bg-[#0d2847] text-[var(--nexus-primary-bright)] border border-blue-500/30">AI-generated</span>}
            </div>
            <div className="space-y-1">
              {(rb.steps || []).map((step, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px]">
                  <span className="w-4 h-4 rounded-full bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] flex items-center justify-center text-[8px] text-[var(--nexus-text-muted)]">{i+1}</span>
                  <span className="text-[var(--nexus-text-secondary)]">{step.action}</span>
                  {step.command && <code className="font-mono text-[9px] text-[var(--nexus-accent)] bg-[var(--nexus-bg-base)] px-1 rounded truncate max-w-[200px]">{step.command}</code>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
